import { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import { ConsentForm, type ConsentFormData } from '../components/submission/ConsentForm';
import { SubmissionConfirmation } from '../components/submission/SubmissionConfirmation';
import {
  Upload,
  File,
  X,
  Loader2,
  AlertCircle,
  Shield,
  Clock,
  Search,
  CheckCircle,
  FolderOpen,
  ArrowLeft,
  BookmarkPlus,
  Star,
} from 'lucide-react';

const MY_CASES_KEY = 'dems:my-cases';
const MAX_MY_CASES = 6;

interface SavedCase {
  caseNumber: string;
  title: string;
  court: string;
  status: string;
  savedAt: string;
}

function loadMyCases(): SavedCase[] {
  try {
    return JSON.parse(localStorage.getItem(MY_CASES_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveToMyCases(c: CaseResult) {
  const existing = loadMyCases().filter(x => x.caseNumber !== c.caseNumber);
  const updated = [{ ...c, savedAt: new Date().toISOString() }, ...existing].slice(0, MAX_MY_CASES);
  localStorage.setItem(MY_CASES_KEY, JSON.stringify(updated));
}

function removeFromMyCases(caseNumber: string) {
  const updated = loadMyCases().filter(x => x.caseNumber !== caseNumber);
  localStorage.setItem(MY_CASES_KEY, JSON.stringify(updated));
}

// Helper function to determine evidence type from MIME type
// Must match DB constraint: Document, Image, Video, Audio, DigitalFile
function getEvidenceTypeFromMime(mimeType: string): string {
  if (mimeType.startsWith('image/')) return 'Image';
  if (mimeType.startsWith('video/')) return 'Video';
  if (mimeType.startsWith('audio/')) return 'Audio';
  if (
    mimeType.startsWith('application/pdf') ||
    mimeType.startsWith('application/msword') ||
    mimeType.startsWith('application/vnd.openxmlformats') ||
    mimeType.startsWith('text/')
  )
    return 'Document';
  return 'DigitalFile';
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

interface SubmissionResult {
  evidenceId: string;
  fileName: string;
  fileHash: string;
  submittedAt: Date;
  caseNumber: string;
  caseTitle: string;
}

interface CaseResult {
  caseNumber: string;
  title: string;
  court: string;
  status: string;
}

// ─── Case Selection mode ─────────────────────────────────────────────────────

function CaseSelection({
  onSelect,
}: {
  onSelect: (c: CaseResult) => void;
}) {
  const [myCases, setMyCases] = useState<SavedCase[]>(() => loadMyCases());
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CaseResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);

  // Refresh my cases whenever localStorage changes (e.g. when user removes one)
  useEffect(() => {
    setMyCases(loadMyCases());
  }, []);

  const handleSearch = async () => {
    if (query.trim().length < 2) return;
    setSearching(true);
    setSearched(false);
    setResults([]);
    try {
      const res = await apiClient.searchPublicCases(query.trim());
      setResults(res);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
      setSearched(true);
    }
  };

  const handleRemoveSaved = (caseNumber: string, e: React.MouseEvent) => {
    e.stopPropagation();
    removeFromMyCases(caseNumber);
    setMyCases(loadMyCases());
  };

  const statusBadge = (status: string) => {
    const base = 'px-2 py-0.5 rounded text-xs font-medium';
    if (status === 'Active') return <span className={`${base} bg-green-100 text-green-700`}>Active</span>;
    if (status === 'Pending Review') return <span className={`${base} bg-orange-100 text-orange-700`}>Pending Review</span>;
    return <span className={`${base} bg-gray-100 text-gray-600`}>{status}</span>;
  };

  const CaseCard = ({ c, saved }: { c: CaseResult | SavedCase; saved?: boolean }) => (
    <button
      type="button"
      onClick={() => onSelect(c)}
      className="w-full text-left p-4 rounded-xl border border-gray-200 hover:border-primary-400 hover:bg-primary-50 transition-all group flex items-start gap-3"
    >
      <div className="p-2 bg-gray-100 group-hover:bg-primary-100 rounded-lg flex-shrink-0 transition-colors">
        <FolderOpen className="h-5 w-5 text-gray-500 group-hover:text-primary-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-gray-900 text-sm">{c.caseNumber}</span>
          {statusBadge(c.status)}
        </div>
        <p className="text-sm text-gray-700 mt-0.5 truncate">{c.title}</p>
        {c.court && <p className="text-xs text-gray-500 mt-0.5">{c.court}</p>}
      </div>
      {saved && (
        <button
          type="button"
          onClick={(e) => handleRemoveSaved(c.caseNumber, e)}
          className="p-1 text-gray-300 hover:text-red-400 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Remove from My Cases"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </button>
  );

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Submit Evidence</h1>
        <p className="mt-1 text-gray-500 text-sm">
          Select the case you want to add evidence to, then you'll be walked through the upload.
        </p>
      </div>

      {/* My Cases */}
      {myCases.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Star className="h-4 w-4 text-amber-500" />
            <h2 className="font-semibold text-gray-800 text-sm uppercase tracking-wide">My Cases</h2>
          </div>
          <div className="grid gap-2">
            {myCases.map(c => (
              <CaseCard key={c.caseNumber} c={c} saved />
            ))}
          </div>
        </section>
      )}

      {/* Search */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Search className="h-4 w-4 text-gray-400" />
          <h2 className="font-semibold text-gray-800 text-sm uppercase tracking-wide">
            {myCases.length > 0 ? 'Search for Another Case' : 'Find Your Case'}
          </h2>
        </div>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="Case number or name  (e.g. 2026-CV-0156)"
              className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <button
            type="button"
            onClick={handleSearch}
            disabled={searching || query.trim().length < 2}
            className="px-4 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2 flex-shrink-0"
          >
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Search
          </button>
        </div>

        {results.length > 0 && (
          <div className="mt-3 grid gap-2">
            {results.map(c => (
              <CaseCard key={c.caseNumber} c={c} />
            ))}
          </div>
        )}

        {searched && !searching && results.length === 0 && (
          <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-500 text-center">
            No cases found for <strong>"{query}"</strong>. Contact court administration if you
            believe your case should appear here.
          </div>
        )}

        {myCases.length === 0 && !searched && (
          <p className="mt-2 text-xs text-gray-400">
            Cases you select will be saved here for quick access next time.
          </p>
        )}
      </section>
    </div>
  );
}

// ─── Upload form mode ─────────────────────────────────────────────────────────

function UploadForm({
  selectedCase,
  onBack,
  onSuccess,
}: {
  selectedCase: CaseResult;
  onBack: () => void;
  onSuccess: (result: SubmissionResult) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [_consent, setConsent] = useState<ConsentFormData | null>(null);
  const [isConsentValid, setIsConsentValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      setError(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    maxSize: 100 * 1024 * 1024,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) { setError('Please select a file to submit.'); return; }
    if (!isConsentValid) { setError('Please acknowledge all required consent items.'); return; }
    if (!description.trim()) { setError('Please provide a description of your submission.'); return; }

    setIsSubmitting(true);
    setError(null);
    try {
      const uploadResult = await apiClient.uploadEvidence(
        selectedCase.caseNumber,
        file,
        { description: description.trim(), evidenceType: getEvidenceTypeFromMime(file.type) }
      );
      onSuccess({
        evidenceId: uploadResult.id,
        fileName: file.name,
        fileHash: uploadResult.sha256Hash || 'Hash calculation pending...',
        submittedAt: new Date(),
        caseNumber: selectedCase.caseNumber,
        caseTitle: selectedCase.title,
      });
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit evidence. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusBadge = (status: string) => {
    const base = 'px-2 py-0.5 rounded text-xs font-medium';
    if (status === 'Active') return <span className={`${base} bg-green-100 text-green-700`}>Active</span>;
    if (status === 'Pending Review') return <span className={`${base} bg-orange-100 text-orange-700`}>Pending Review</span>;
    return <span className={`${base} bg-gray-100 text-gray-600`}>{status}</span>;
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Case context bar */}
      <div className="flex items-center gap-3 mb-6 p-4 bg-white rounded-xl border border-gray-200">
        <button
          type="button"
          onClick={onBack}
          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex-shrink-0"
          title="Change case"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex-1 flex items-center gap-3 min-w-0">
          <FolderOpen className="h-5 w-5 text-primary-600 flex-shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900 text-sm">{selectedCase.caseNumber}</span>
              {statusBadge(selectedCase.status)}
            </div>
            <p className="text-xs text-gray-500 truncate">{selectedCase.title}</p>
          </div>
        </div>
        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
      </div>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Submit Evidence</h1>
        <p className="mt-1 text-gray-500 text-sm">
          All submissions are securely stored with full chain of custody tracking.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Step 1 — File */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">1. Select File</h2>
          </div>
          <div className="p-6">
            {!file ? (
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  isDragActive
                    ? 'border-justice-navy bg-justice-navy/5'
                    : 'border-gray-300 hover:border-justice-navy/50 hover:bg-gray-50'
                }`}
              >
                <input {...getInputProps()} />
                <Upload className={`mx-auto h-12 w-12 ${isDragActive ? 'text-justice-navy' : 'text-gray-400'}`} />
                <p className="mt-4 text-sm text-gray-600">
                  {isDragActive ? (
                    <span className="text-justice-navy font-medium">Drop file here...</span>
                  ) : (
                    <><span className="text-justice-navy font-medium">Click to upload</span> or drag and drop</>
                  )}
                </p>
                <p className="mt-2 text-xs text-gray-500">Images, videos, documents, audio · Max 100 MB</p>
              </div>
            ) : (
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <File className="h-10 w-10 text-justice-navy" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{file.name}</p>
                  <p className="text-sm text-gray-500">{formatFileSize(file.size)}</p>
                </div>
                <button type="button" onClick={() => setFile(null)} className="p-2 text-gray-400 hover:text-red-500 transition-colors">
                  <X className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Step 2 — Description */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">2. Describe Your Submission</h2>
          </div>
          <div className="p-6">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-justice-navy/20 focus:border-justice-navy transition-colors resize-none"
              placeholder="Describe what this evidence shows, relevant dates, locations, or people involved."
              required
            />
          </div>
        </div>

        {/* Step 3 — Consent */}
        <div>
          <div className="mb-4">
            <h2 className="font-semibold text-gray-900">3. Consent &amp; Acknowledgment</h2>
          </div>
          <ConsentForm onConsentChange={setConsent} onValidChange={setIsConsentValid} />
        </div>

        {/* Security notice */}
        <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
          <Shield className="h-5 w-5 text-justice-navy flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-600">
            <p className="font-medium text-gray-900">Secure Submission</p>
            <p className="mt-1">
              Your file is encrypted in transit and stored with WORM immutability. A SHA-256
              cryptographic fingerprint is generated automatically.
            </p>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg border border-red-200">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={isSubmitting || !file || !isConsentValid || !description.trim()}
            className="inline-flex items-center gap-2 px-8 py-3 bg-justice-navy text-white font-medium rounded-lg hover:bg-justice-navy/90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? (
              <><Loader2 className="h-5 w-5 animate-spin" />Submitting...</>
            ) : (
              <><Upload className="h-5 w-5" />Submit Evidence</>
            )}
          </button>
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-500 justify-center">
          <Clock className="h-4 w-4" />
          <span>Submissions are typically reviewed within 2–3 business days</span>
        </div>
      </form>
    </div>
  );
}

// ─── Page root ────────────────────────────────────────────────────────────────

export default function PublicSubmission() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const caseParam = searchParams.get('case');
  
  const [selectedCase, setSelectedCase] = useState<CaseResult | null>(null);
  const [submissionResult, setSubmissionResult] = useState<SubmissionResult | null>(null);
  const [loadingCaseParam, setLoadingCaseParam] = useState(!!caseParam);

  // Auto-load case from query param
  useEffect(() => {
    if (caseParam && !selectedCase) {
      setLoadingCaseParam(true);
      apiClient.getCase(caseParam)
        .then(c => {
          setSelectedCase({
            caseNumber: c.caseNumber,
            title: c.title,
            court: c.court || '',
            status: c.status,
          });
        })
        .catch(() => {
          // Case not found, fall back to selection
        })
        .finally(() => setLoadingCaseParam(false));
    }
  }, [caseParam, selectedCase]);

  const handleSelectCase = (c: CaseResult) => {
    saveToMyCases(c);
    setSelectedCase(c);
  };

  const handleBack = () => {
    // If came from case param, go back to case detail
    if (caseParam && selectedCase) {
      window.history.back();
      return;
    }
    setSelectedCase(null);
    setSubmissionResult(null);
  };

  if (loadingCaseParam) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (submissionResult) {
    return (
      <div className="py-8">
        <SubmissionConfirmation
          evidenceId={submissionResult.evidenceId}
          fileName={submissionResult.fileName}
          fileHash={submissionResult.fileHash}
          submittedAt={submissionResult.submittedAt}
          contactEmail={user?.email}
        />
        <div className="max-w-2xl mx-auto mt-6 text-center">
          {caseParam ? (
            <Link
              to={`/cases/${caseParam}`}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-800"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to case
            </Link>
          ) : (
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-800"
            >
              <BookmarkPlus className="h-4 w-4" />
              Submit another item to this case
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!selectedCase) {
    return (
      <div className="py-8 px-4">
        <CaseSelection onSelect={handleSelectCase} />
      </div>
    );
  }

  return (
    <div className="py-8 px-4">
      <UploadForm
        selectedCase={selectedCase}
        onBack={handleBack}
        onSuccess={setSubmissionResult}
      />
    </div>
  );
}

